import { ReactNode, useContext, useEffect, useRef } from 'react';
import { MicrositeContext } from 'context';
import { useEnvironment } from 'services/environment';
import { reportError } from 'services/datadog/data-dog-rum';
import { decideHostAccess } from 'utils/whitelist/decideHostAccess';
import { resolveEmbeddingOrigin } from 'utils/whitelist/resolveEmbeddingOrigin';

export class HostNotWhitelistedError extends Error {
  /** Every frame in the embedding chain the whitelist does not name. */
  readonly untrusted: readonly string[];

  constructor(untrusted: readonly string[]) {
    super('host is not whitelisted');
    this.name = 'HostNotWhitelistedError';
    this.untrusted = untrusted;
  }
}

/**
 * Reports a refused host, for the error boundary's `onError`.
 */
export function reportHostAccessError(error: Error) {
  if (error instanceof HostNotWhitelistedError) {
    reportError(error, { untrustedOrigins: error.untrusted.join(', ') });
  }
}

/**
 * Stops the app when the page embedding it is not one the partner's config
 * vouches for.
 */
export function WhitelistGuard({ children }: { children: ReactNode }) {
  const { name } = useEnvironment();
  const micrositeConfig = useContext(MicrositeContext);
  const hasReportedUnverified = useRef(false);

  const decision = decideHostAccess({
    env: String(name),
    whitelist: micrositeConfig?.iFrameConfig?.trustedHosts?.production,
    embedding: resolveEmbeddingOrigin(),
  });

  // Unlike a refusal, this case lets the app carry on, so this component will
  // render again many times. The ref makes sure the report is sent once rather
  // than once per render.
  useEffect(() => {
    if (decision.outcome === 'unverified' && !hasReportedUnverified.current) {
      hasReportedUnverified.current = true;
      reportError(new Error('embedding host could not be determined'));
    }
  }, [decision.outcome]);

  if (decision.outcome === 'denied') {
    throw new HostNotWhitelistedError(decision.untrusted);
  }

  return <>{children}</>;
}
